import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ChevronLeft, Loader as LoaderIcon, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/lib/i18n";
import { Logo } from "@/components/layout/PublicLayout";

export default function Login() {
  const { t } = useLang();
  const { login, complete2FA } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // 2FA step state
  const [preAuthToken, setPreAuthToken] = useState(null);
  const [code, setCode] = useState("");
  const [useBackup, setUseBackup] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await login(email, password);
      if (result.requires2FA) {
        setPreAuthToken(result.preAuthToken);
        setCode("");
        setUseBackup(false);
      } else if (result.mustSetup2FA) {
        navigate("/2fa-einrichten");
      } else if (result.mustChangePassword) {
        navigate("/passwort-aendern");
      } else {
        toast.success(t("Willkommen zur\u00fcck!", "Welcome back!"));
        navigate("/portal");
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("Anmeldung fehlgeschlagen", "Login failed"));
    } finally {
      setLoading(false);
    }
  };

  const submit2FA = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const result = await complete2FA(preAuthToken, code);
      if (result.mustChangePassword) {
        navigate("/passwort-aendern");
        return;
      }
      toast.success(t("Willkommen zur\u00fcck!", "Welcome back!"));
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail || t("Best\u00e4tigung fehlgeschlagen", "Verification failed");
      setError(detail);
      // Zwischentoken abgelaufen -> zurueck zu Schritt 1
      if (err.response?.status === 401 && detail.includes("abgelaufen")) {
        setPreAuthToken(null);
        setPassword("");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen dark-section noise-overlay flex items-center justify-center px-4">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="flex justify-center">
          <Logo dark />
        </div>
        <Card className="mt-8 rounded-[8px] border-white/10 bg-white/[0.06] backdrop-blur-md">
          <CardContent className="p-8">
            {!preAuthToken ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-[8px] bg-[#FF3C20]/12 flex items-center justify-center">
                    <Lock className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <h1 className="font-display text-xl font-semibold text-white">{t("Mitarbeiter-Login", "Employee Login")}</h1>
                    <p className="text-xs text-white/50">{t("Mitarbeiterportal \u00b7 Academy \u00b7 Admin Center", "Employee Portal \u00b7 Academy \u00b7 Admin Center")}</p>
                  </div>
                </div>
                <form onSubmit={submit} className="mt-7 space-y-5">
                  <div className="space-y-1.5">
                    <Label className="text-white/80">E-Mail</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="rounded-[6px] bg-white/5 border-white/15 text-white placeholder:text-white/30"
                      placeholder="name@eleven-familyoffice.ch"
                      data-testid="login-email-input"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-white/80">{t("Passwort", "Password")}</Label>
                    <Input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="rounded-[6px] bg-white/5 border-white/15 text-white"
                      data-testid="login-password-input"
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
                  )}
                  <Button type="submit" disabled={loading} className="w-full rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="login-submit-button">
                    {loading ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Anmelden", "Sign in")}
                  </Button>
                  <div className="text-center">
                    <Link to="/passwort-vergessen" className="text-xs text-white/50 hover:text-white transition-colors underline-offset-2 hover:underline" data-testid="login-forgot-password-link">
                      {t("Passwort vergessen?", "Forgot password?")}
                    </Link>
                  </div>
                </form>
              </>
            ) : (
              <>
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-[8px] bg-[#FF3C20]/12 flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5 text-[#FF3C20]" />
                  </div>
                  <div>
                    <h1 className="font-display text-xl font-semibold text-white">{t("Zwei-Faktor-Best\u00e4tigung", "Two-Factor Verification")}</h1>
                    <p className="text-xs text-white/50">
                      {useBackup
                        ? t("Geben Sie einen Ihrer Backup-Codes ein", "Enter one of your backup codes")
                        : t("Code aus Ihrer Authenticator-App eingeben", "Enter the code from your authenticator app")}
                    </p>
                  </div>
                </div>
                <form onSubmit={submit2FA} className="mt-7 space-y-5">
                  <div className="space-y-1.5">
                    <Label className="text-white/80">
                      {useBackup ? t("Backup-Code", "Backup code") : t("6-stelliger Code", "6-digit code")}
                    </Label>
                    <Input
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      required
                      autoFocus
                      inputMode={useBackup ? "text" : "numeric"}
                      maxLength={useBackup ? 9 : 6}
                      placeholder={useBackup ? "XXXX-XXXX" : "123456"}
                      className="rounded-[6px] bg-white/5 border-white/15 text-white text-center text-lg tracking-[0.3em] placeholder:tracking-normal placeholder:text-white/30"
                      data-testid="login-2fa-code-input"
                    />
                  </div>
                  {error && (
                    <p className="text-sm text-red-400" data-testid="login-2fa-error">{error}</p>
                  )}
                  <Button type="submit" disabled={loading || code.length < 4} className="w-full rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] active:scale-[0.98]" data-testid="login-2fa-submit-button">
                    {loading ? <LoaderIcon className="h-4 w-4 animate-spin" /> : t("Best\u00e4tigen", "Verify")}
                  </Button>
                  <div className="flex items-center justify-between text-xs">
                    <button
                      type="button"
                      onClick={() => { setUseBackup(!useBackup); setCode(""); setError(""); }}
                      className="text-white/50 hover:text-white transition-colors underline-offset-2 hover:underline"
                      data-testid="login-2fa-backup-toggle"
                    >
                      {useBackup ? t("Authenticator-Code verwenden", "Use authenticator code") : t("Backup-Code verwenden", "Use a backup code")}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setPreAuthToken(null); setCode(""); setError(""); setPassword(""); }}
                      className="text-white/50 hover:text-white transition-colors underline-offset-2 hover:underline"
                      data-testid="login-2fa-back-button"
                    >
                      {t("Zur\u00fcck", "Back")}
                    </button>
                  </div>
                </form>
              </>
            )}
          </CardContent>
        </Card>
        <div className="mt-6 text-center">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-white/50 hover:text-white transition-colors" data-testid="login-back-link">
            <ChevronLeft className="h-4 w-4" /> {t("Zur\u00fcck zur Website", "Back to website")}
          </Link>
        </div>
      </motion.div>
    </div>
  );
}
